import { forwardRef, HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react';

import { cn } from './cn';

export const TableContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, 'aria-label': ariaLabel, ...props }, ref) => {
    // An unnamed region called "Table" collides when two sit on one page.
    // Only a caller that passes a name gets the landmark.
    const shared = {
      ref,
      className: cn('scrollbar-muted relative w-full min-w-0 max-w-full overflow-auto', className),
      ...props,
    };
    if (ariaLabel) {
      return (
        <div
          {...shared}
          role="region"
          aria-label={ariaLabel}
          // A keyboard user can only scroll an overflow region by focusing it.
          // biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be in the tab order
          tabIndex={0}
        />
      );
    }
    return (
      <div
        {...shared}
        // A keyboard user can only scroll an overflow region by focusing it.
        // biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be in the tab order
        tabIndex={0}
      />
    );
  },
);
TableContainer.displayName = 'TableContainer';

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(({ className, ...props }, ref) => (
  <table ref={ref} className={cn('w-full border-collapse text-left text-sm text-ink', className)} {...props} />
));
Table.displayName = 'Table';

export const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn('bg-canvas', className)} {...props} />,
);
TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={className} {...props} />,
);
TableBody.displayName = 'TableBody';

export const TableFooter = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn('border-t border-line bg-canvas font-medium', className)} {...props} />
  ),
);
TableFooter.displayName = 'TableFooter';

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-line transition-colors last:border-b-0 data-[state=selected]:bg-brand-subtle',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'h-10 whitespace-nowrap border-b border-line px-3 text-left align-middle text-xs font-semibold uppercase',
        'tracking-eyebrow text-ink-muted',
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => <td ref={ref} className={cn('px-3 py-2 align-middle', className)} {...props} />,
);
TableCell.displayName = 'TableCell';

export const TableCaption = forwardRef<HTMLTableCaptionElement, HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('mt-4 text-sm text-ink-muted', className)} {...props} />
  ),
);
TableCaption.displayName = 'TableCaption';
